/**
 * Import/export CSV : template, dépôt de fichier ou collage de texte, aperçu
 * systématique (`/import/preview`) avant application, puis confirmation.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { appAlert } from '../dialogs/service.js';
import { Eyebrow, Panel } from '../components/common.jsx';
import { BTN_GHOST_SM, TD, TEXTAREA, TH } from '../ui/theme.js';

export function TransferPanel({ projectId, projectKey }) {
  const { t } = useTranslation();
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  function readFile(file) {
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.readAsText(file, 'utf-8');
  }

  function onDrop(event) {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) readFile(file);
  }

  async function runPreview() {
    if (!csv.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/transfer/import/preview`, { csv });
      setPreview(data);
    } catch (error) {
      await appAlert(t('transfer.import.previewFailed', { message: error.message }));
    } finally {
      setBusy(false);
    }
  }

  async function confirmImport() {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/transfer/import`, { csv });
      await appAlert(t('transfer.import.applied'));
      setPreview(null);
      setCsv('');
    } catch (error) {
      await appAlert(t('transfer.import.importFailed', { message: error.message }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Panel>
        <Eyebrow>{t('transfer.template.title')}</Eyebrow>
        <p className="mt-2 text-sm text-ink-soft">{t('transfer.template.description')}</p>
        <button
          type="button"
          className={`${BTN_GHOST_SM} mt-3`}
          onClick={() =>
            api.download(
              `/projects/${projectId}/transfer/template.csv`,
              'modele-import-exiplan.csv',
            )
          }
        >
          {t('transfer.template.download')}
        </button>
      </Panel>

      <Panel>
        <Eyebrow>{t('transfer.import.title')}</Eyebrow>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="mt-3 flex min-h-24 items-center justify-center border-2 border-line bg-surface p-4 text-center text-sm text-ink-soft"
        >
          {/* Zone de dépôt, pas un état vide : pas de hachure (`@exidis/ui` 1.3.0 — la hachure
              est réservée aux cellules et listes sans données). */}
          <label className="cursor-pointer px-2">
            {t('transfer.import.dropText')}{' '}
            <span className="underline">
              {t('transfer.import.browse')}
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])}
              />
            </span>
          </label>
        </div>
        <textarea
          className={`${TEXTAREA} mt-3 w-full font-mono`}
          rows={6}
          placeholder={t('transfer.import.textareaPlaceholder')}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          aria-label={t('transfer.import.textareaAria')}
        />
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className={BTN_GHOST_SM}
            disabled={busy || !csv.trim()}
            onClick={runPreview}
          >
            {t('transfer.import.preview')}
          </button>
          {preview && (
            <button
              type="button"
              className={BTN_GHOST_SM}
              disabled={busy || preview.summary.enErreur > 0}
              onClick={confirmImport}
            >
              {t('transfer.import.confirm')}
            </button>
          )}
        </div>

        {preview && (
          <div className="mt-3">
            <p className="font-mono text-[11px] uppercase tracking-label text-ink-faint">
              {t('transfer.import.summary', {
                toCreate: preview.summary.aCreer,
                toUpdate: preview.summary.aMettreAJour,
                errors: preview.summary.enErreur,
              })}
            </p>
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('transfer.import.columns.line')}</th>
                  <th className={TH}>{t('transfer.import.columns.title')}</th>
                  <th className={TH}>{t('transfer.import.columns.action')}</th>
                  <th className={TH}>{t('transfer.import.columns.messages')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.ligne}>
                    <td className={TD}>{row.ligne}</td>
                    <td className={TD}>{row.titre || '—'}</td>
                    <td className={TD}>{row.action}</td>
                    <td className={TD}>
                      {row.erreurs.map((message) => (
                        <p key={message} className="text-accent-ink">
                          {message}
                        </p>
                      ))}
                      {row.avertissements.map((message) => (
                        <p key={message} className="text-ink-faint">
                          {message}
                        </p>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel>
        <Eyebrow>{t('transfer.export.title')}</Eyebrow>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            className={BTN_GHOST_SM}
            onClick={() =>
              api.download(
                `/projects/${projectId}/transfer/export.csv`,
                `${projectKey ?? 'export'}-export.csv`,
              )
            }
          >
            {t('transfer.export.csv')}
          </button>
          <button
            type="button"
            className={BTN_GHOST_SM}
            onClick={() =>
              api.download(
                `/projects/${projectId}/transfer/export.pdf`,
                `${projectKey ?? 'export'}-rapport.pdf`,
              )
            }
          >
            {t('transfer.export.pdf')}
          </button>
        </div>
      </Panel>
    </div>
  );
}
